<!-- Wrapper container for toast notifications -->
<template>
  <div
    id="toast-notifications"
    :class="['mx-lgp mx-md-0 shadow-secondary', {
      'above-floating-buttons': floatingButtonsPresent
    }]">
    <TransitionGroup
      name="toast-notification-list"
      tag="div"
      class="toast-notification-list">
      <ToastNotificationListItem
        v-for="(notification) in visibleToastNotifications"
        :key="notification.id"
        :type="notification.type"
        :message="notification.message"
        :link="notification.link"
        :centered="notification.centered"
        :class="{ 'pointer': notification.onClick }"
        @click="notification.onClick">
        <template #icon>
          <component :is="notification.icon"/>
        </template>
      </ToastNotificationListItem>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'
import ToastNotificationListItem from './ToastNotificationListItem.vue'
import { useToastNotificationsStore } from '@/stores/toastNotifications'
import { useElementPresence } from '@/composables/element.js'
import useScreenSize from '@shared/composables/screenSize.js'
import { useHideIntercomLauncher } from '@shared/composables/intercom.js'

const toastNotificationsStore = useToastNotificationsStore()
const { visibleToastNotifications } = storeToRefs(toastNotificationsStore)

const floatingButtonsPresent = useElementPresence('.floating-buttons')
const { isMobile } = useScreenSize()

// Hide Intercom launcher when there are toast notifications with links so it's not in the way
useHideIntercomLauncher(() =>
  isMobile.value
  && visibleToastNotifications.value.some(notification => !!notification.link)
  && !floatingButtonsPresent.value // Floating buttons push toast notifications above the Intercom launcher
)
</script>

<style lang="scss" scoped>
#toast-notifications {
  position: absolute;
  right: 0;
  z-index: $zindex-fixed;
  width: 425px;
  display: flex;
  flex-direction: column;

  --toast-notifications-bottom: 24px;

  @include media-mobile {
    position: fixed;
    left: 0;
    right: 0;
    bottom: var(--toast-notifications-bottom);
    width: unset;

    &.above-floating-buttons {
      bottom: calc(var(--toast-notifications-bottom) + 60px);
    }
  }

  .toast-notification-list {
    --animation-speed: 0.4s;

    &-move,
    &-enter-active,
    &-leave-active {
      transition: all var(--animation-speed) ease;
    }

    &-enter-from,
    &-leave-to {
      opacity: 0;
      transform: translateX(100%);
    }

    &-leave-active {
      position: absolute;
      width: 100%;
    }

    @include media-mobile {
      .toast-notification-list {
        &-enter-from,
        &-leave-to {
          transform: translateY(100%);
        }
      }
    }
  }
}
</style>
